import { Bell, CalendarPlus, Menu, WifiOff } from 'lucide-react'
import { Suspense, useEffect, useState } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router'
import { LogoMark } from '@/components/brand/logo'
import { ErrorState } from '@/components/common/states'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { buildAlerts } from '@/features/notifications/alerts'
import { AlertRow } from '@/features/notifications/alert-row'
import { useWorkspaceQuery } from '@/hooks/queries'
import { isMockData } from '@/infrastructure/config'
import { cn } from '@/lib/utils'
import { ErrorBoundary } from '../error-boundary'
import { useSession } from '../session'
import { useWs, WorkspaceProvider } from '../workspace'
import { NAV_ADMIN, NAV_PRIMARY, NAV_TEAM } from './nav'
import { Sidebar } from './sidebar'

function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])
  return online
}

function NotificationsPopover() {
  const ws = useWs()
  const alerts = buildAlerts(ws)
  const urgent = alerts.filter((a) => a.tone === 'danger' || a.kind === 'approval').length
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Avisos${alerts.length ? ` (${alerts.length})` : ''}`} className="relative">
          <Bell />
          {urgent > 0 && <span className="absolute top-1 right-1 size-2 rounded-full bg-accent ring-2 ring-bg" aria-hidden />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <p className="border-b border-border px-4 py-2.5 text-sm font-semibold">Avisos</p>
        {alerts.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-fg-muted">Tudo em dia. Sem avisos.</p>
        ) : (
          <ul className="max-h-96 overflow-y-auto p-1.5 scrollbar-thin">
            {alerts.map((a) => (
              <li key={a.id}>
                <AlertRow alert={a} compact />
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  )
}

function MobileNav({ onMore }: { onMore: () => void }) {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden" aria-label="Navegação">
      {NAV_PRIMARY.filter((i) => i.mobile).map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.to === '/'}
          className={({ isActive }) => cn('flex flex-1 flex-col items-center gap-0.5 py-2 text-2xs', isActive ? 'text-fg' : 'text-fg-subtle')}
        >
          <i.icon className="size-5" />
          {i.label.replace('Os meus pedidos', 'Pedidos').replace('Marcar férias', 'Marcar')}
        </NavLink>
      ))}
      <button className="flex flex-1 flex-col items-center gap-0.5 py-2 text-2xs text-fg-subtle" onClick={onMore}>
        <Menu className="size-5" />
        Mais
      </button>
    </nav>
  )
}

function Shell() {
  const ws = useWs()
  const [moreOpen, setMoreOpen] = useState(false)
  const online = useOnline()
  const location = useLocation()
  useEffect(() => {
    setMoreOpen(false)
  }, [location.pathname])
  const extra = [...NAV_TEAM, ...NAV_ADMIN].filter((i) => !i.permission || ws.can(i.permission))

  return (
    <div className="flex h-dvh overflow-hidden">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2">
        Saltar para o conteúdo
      </a>
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-bg/80 px-3 backdrop-blur md:px-4">
          <Link to="/" className="flex min-w-0 items-center gap-2 md:hidden" aria-label="Visão geral">
            <LogoMark />
            <span className="truncate text-sm font-semibold">{ws.data.organization.name}</span>
          </Link>
          <div className="min-w-0 flex-1">
            {isMockData() && (
              <span className="hidden rounded-md border border-dashed border-border-strong px-2 py-0.5 text-2xs text-fg-subtle sm:inline">
                Dados de demonstração · guardados neste browser
              </span>
            )}
          </div>
          <NotificationsPopover />
          {location.pathname !== '/new' && (
            <Button asChild variant="primary" size="sm">
              <Link to="/new">
                <CalendarPlus /> <span className="hidden sm:inline">Marcar férias</span>
              </Link>
            </Button>
          )}
        </header>
        {!online && (
          <div className="flex items-center justify-center gap-2 bg-warning-soft px-3 py-1.5 text-xs text-warning" role="status">
            <WifiOff className="size-3.5" /> Sem ligação. As alterações não serão guardadas.
          </div>
        )}
        <main id="main" className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-16 md:pb-0">
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      <MobileNav onMore={() => setMoreOpen(true)} />
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent width="md">
          <SheetTitle className="px-4 pt-4 text-sm font-semibold">Menu</SheetTitle>
          <nav className="grid gap-0.5 p-3">
            {[...NAV_PRIMARY, ...extra].map((i) => (
              <NavLink key={i.to} to={i.to} end={i.to === '/'} className={({ isActive }) => cn('flex h-10 items-center gap-3 rounded-md px-3 text-sm', isActive ? 'bg-hover font-medium' : 'text-fg-muted')}>
                <i.icon className="size-4" /> {i.label}
              </NavLink>
            ))}
            <NavLink to="/organizations" className="flex h-10 items-center gap-3 rounded-md px-3 text-sm text-fg-muted">
              Mudar de organização
            </NavLink>
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export function PageSkeleton() {
  return (
    <div className="grid gap-4 p-4 md:p-8" aria-busy>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-80" />
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
      </div>
      <Skeleton className="h-64" />
    </div>
  )
}

/** Signed in + organization chosen + workspace loaded → app shell. */
export function AppLayout() {
  const { session, membership, memberships, membershipsLoading, signedOut } = useSession()
  const location = useLocation()
  const ws = useWorkspaceQuery(membership?.organization.id)

  if (session === undefined || (session && membershipsLoading)) return <FullPageLoading />
  if (!session) return <Navigate to="/login" replace state={signedOut ? undefined : { from: location.pathname + location.search }} />
  if (!memberships?.length) return <Navigate to="/onboarding" replace />
  if (!membership) return <Navigate to="/organizations" replace />
  if (ws.isError) return <ErrorState error={ws.error} onRetry={() => void ws.refetch()} className="min-h-dvh justify-center" />
  if (!ws.data) return <FullPageLoading />
  return (
    <WorkspaceProvider data={ws.data}>
      <Shell />
    </WorkspaceProvider>
  )
}

export function FullPageLoading() {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-label="A carregar">
      <LogoMark className="size-8 animate-pulse" />
    </div>
  )
}
